import { createFileRoute } from "@tanstack/react-router"
import type { ColumnDef } from "@tanstack/react-table"
import { useState } from "react"
import { Page } from "@/components/page"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { DataTable } from "@/components/ui/data-table"
import { Typography } from "@/components/ui/typography"
import { trpc } from "@/lib/trpc"

export const Route = createFileRoute("/_authed/_individual/personal/collaborators")({
  component: CollaboratorsPage,
})

interface Collaborator {
  id: string
  firstName: string | null
  lastName: string | null
  email: string
  role: string
  estateId: string
  userType: string
}

function CollaboratorsPage() {
  const [revokingCollaborator, setRevokingCollaborator] = useState<string | null>(null)

  const { data: collaborators = [], isLoading, refetch } = trpc.users.listCollaborators.useQuery()

  const revokeCollaboratorMutation = trpc.users.revokeCollaborator.useMutation({
    onSuccess: () => {
      refetch()
      setRevokingCollaborator(null)
    },
    onError: (_error) => {
      setRevokingCollaborator(null)
    },
  })

  const handleRevokeCollaborator = (collaboratorUserId: string, estateId: string) => {
    if (
      window.confirm(
        "Are you sure you want to revoke this collaborator's access? This action cannot be undone.",
      )
    ) {
      setRevokingCollaborator(collaboratorUserId)
      revokeCollaboratorMutation.mutate({
        collaboratorUserId,
        estateId,
      })
    }
  }

  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case "ADMIN":
        return "default"
      case "STANDARD":
        return "secondary"
      default:
        return "secondary"
    }
  }

  const columns: ColumnDef<Collaborator>[] = [
    {
      accessorKey: "firstName",
      header: "First Name",
      cell: ({ row }) => {
        const firstName = row.getValue("firstName") as string | null
        return firstName || "-"
      },
    },
    {
      accessorKey: "lastName",
      header: "Last Name",
      cell: ({ row }) => {
        const lastName = row.getValue("lastName") as string | null
        return lastName || "-"
      },
    },
    {
      accessorKey: "email",
      header: "Email",
    },
    {
      accessorKey: "role",
      header: "Role",
      cell: ({ row }) => {
        const role = row.getValue("role") as string
        return <Badge variant={getRoleBadgeVariant(role)}>{role}</Badge>
      },
    },
    {
      accessorKey: "userType",
      header: "User Type",
      cell: ({ row }) => {
        const userType = row.getValue("userType") as string
        return <Badge variant="outline">{userType}</Badge>
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const collaborator = row.original
        const isRevoking = revokingCollaborator === collaborator.id

        return (
          <Button
            variant="destructiveOutline"
            size="sm"
            onClick={() => handleRevokeCollaborator(collaborator.id, collaborator.estateId)}
            disabled={isRevoking}
          >
            {isRevoking ? "Revoking..." : "Revoke Access"}
          </Button>
        )
      },
    },
  ]

  return (
    <Page
      headerSlot={
        <Typography as="h1" size="2xl">
          My Collaborators
        </Typography>
      }
    >
      <div className="space-y-8">
        <div className="rounded-lg border p-6">
          <Typography as="h2" size="lg" className="mb-4">
            Connected Professionals
          </Typography>

          {isLoading ? (
            <div>Loading collaborators...</div>
          ) : (
            <DataTable columns={columns} data={collaborators} />
          )}
        </div>
      </div>
    </Page>
  )
}
